import React from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import AudiotrackRoundedIcon from "@mui/icons-material/AudiotrackRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CircularProgress from "@mui/material/CircularProgress";
import omnichannelInboxApi from "../../services/omnichannelInboxApi";

const formatFileSize = (value) => {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) {
    return "";
  }

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const iconForAttachment = (kind) => {
  switch (kind) {
    case "image":
      return <ImageRoundedIcon sx={{ fontSize: 20 }} />;
    case "sticker":
      return <ImageRoundedIcon sx={{ fontSize: 20 }} />;
    case "audio":
      return <AudiotrackRoundedIcon sx={{ fontSize: 20 }} />;
    case "video":
      return <VideoLibraryRoundedIcon sx={{ fontSize: 20 }} />;
    default:
      return <InsertDriveFileRoundedIcon sx={{ fontSize: 20 }} />;
  }
};

function AttachmentCard({ attachment, compact = false }) {
  const [pendingAction, setPendingAction] = React.useState("");
  const [actionError, setActionError] = React.useState("");
  const kind = String(attachment?.kind || "file").toLowerCase();
  const previewUrl = attachment?.preview_url || attachment?.download_url || "";
  const downloadUrl = attachment?.download_url || previewUrl || "";
  const fileName = attachment?.name || "Attachment";
  const showStickerPreview = kind === "sticker" && previewUrl;
  const showImagePreview = (kind === "image" || kind === "sticker") && previewUrl;
  const showAudioPlayer = kind === "audio" && previewUrl && !attachment?.is_voice_note;
  const showVideoPlayer = kind === "video" && previewUrl;
  const loadingView = pendingAction === "view";
  const loadingDownload = pendingAction === "download";

  const releaseObjectUrlLater = React.useCallback((objectUrl) => {
    window.setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 60_000);
  }, []);

  const handleView = React.useCallback(async () => {
    const sourceUrl = previewUrl || downloadUrl;
    if (!sourceUrl) {
      return;
    }

    const popup = window.open("", "_blank", "noopener,noreferrer");
    if (popup) {
      popup.document.title = fileName;
      popup.document.body.innerHTML = "<p style=\"font-family: sans-serif; padding: 16px;\">Loading attachment...</p>";
    }

    setPendingAction("view");
    setActionError("");

    try {
      const { blob } = await omnichannelInboxApi.fetchProtectedAttachment(sourceUrl);
      const objectUrl = URL.createObjectURL(blob);

      if (popup) {
        popup.location.replace(objectUrl);
      } else {
        window.open(objectUrl, "_blank", "noopener,noreferrer");
      }

      releaseObjectUrlLater(objectUrl);
    } catch (error) {
      if (popup) {
        popup.close();
      }

      setActionError(error?.response?.data?.message || error?.message || "Unable to open this attachment right now.");
    } finally {
      setPendingAction("");
    }
  }, [downloadUrl, fileName, previewUrl, releaseObjectUrlLater]);

  const handleDownload = React.useCallback(async () => {
    const sourceUrl = downloadUrl || previewUrl;
    if (!sourceUrl) {
      return;
    }

    setPendingAction("download");
    setActionError("");

    try {
      const { blob } = await omnichannelInboxApi.fetchProtectedAttachment(sourceUrl);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      releaseObjectUrlLater(objectUrl);
    } catch (error) {
      setActionError(error?.response?.data?.message || error?.message || "Unable to download this attachment right now.");
    } finally {
      setPendingAction("");
    }
  }, [downloadUrl, fileName, previewUrl, releaseObjectUrlLater]);

  if (showStickerPreview) {
    return (
      <Box
        sx={{
          display: "inline-flex",
          width: "fit-content",
          maxWidth: 152,
          p: 0.15,
        }}
      >
        <Box
          component="img"
          src={previewUrl}
          alt={fileName}
          sx={{
            display: "block",
            width: "100%",
            maxWidth: 144,
            maxHeight: 144,
            objectFit: "contain",
            bgcolor: "transparent",
          }}
        />
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: compact ? 1.6 : 2.5,
        border: compact ? "none" : "1px solid #DDE7EF",
        bgcolor: "#FFFFFF",
        overflow: "hidden",
        boxShadow: compact ? "0 1px 1px rgba(17,27,33,0.08)" : "none",
      }}
    >
      {showImagePreview ? (
        <Box
          component="img"
          src={previewUrl}
          alt={fileName}
          sx={{
            display: "block",
            width: "100%",
            maxHeight: 220,
            objectFit: "cover",
            bgcolor: "#F4F7FA",
          }}
        />
      ) : null}

      <Box sx={{ px: 1.15, py: 1 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "12px",
              bgcolor: "#F2F7FC",
              color: "#4C6278",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {iconForAttachment(kind)}
          </Box>

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#17324A", wordBreak: "break-word" }}>
              {fileName}
            </Typography>
            <Typography variant="caption" sx={{ color: "#6A7C8E", display: "block", mt: 0.2 }}>
              {[attachment?.mime_type, formatFileSize(attachment?.size_bytes)].filter(Boolean).join(" • ") || "Attachment"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            {previewUrl ? (
              <IconButton
                size="small"
                onClick={handleView}
                disabled={loadingView || loadingDownload}
                sx={{ width: 32, height: 32 }}
              >
                {loadingView ? <CircularProgress size={16} /> : <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            ) : null}
            {downloadUrl ? (
              <IconButton
                size="small"
                onClick={handleDownload}
                disabled={loadingView || loadingDownload}
                sx={{ width: 32, height: 32 }}
              >
                {loadingDownload ? <CircularProgress size={16} /> : <DownloadRoundedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            ) : null}
          </Stack>
        </Stack>

        {showAudioPlayer ? (
          <Box sx={{ mt: 1 }}>
            <audio controls preload="metadata" src={previewUrl} style={{ width: "100%" }}>
              Your browser does not support audio playback.
            </audio>
          </Box>
        ) : null}

        {showVideoPlayer ? (
          <Box sx={{ mt: 1 }}>
            <video controls preload="metadata" src={previewUrl} style={{ width: "100%", borderRadius: 12, background: "#000" }}>
              Your browser does not support video playback.
            </video>
          </Box>
        ) : null}

        {!showAudioPlayer && !showVideoPlayer && !showImagePreview && downloadUrl ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleView}
              disabled={loadingView || loadingDownload}
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
            >
              {loadingView ? "Opening..." : "View"}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={handleDownload}
              disabled={loadingView || loadingDownload}
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
            >
              {loadingDownload ? "Downloading..." : "Download"}
            </Button>
          </Stack>
        ) : null}

        {actionError ? (
          <Typography variant="caption" sx={{ display: "block", mt: 0.85, color: "#B94A48" }}>
            {actionError}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}

export default function MessageAttachmentGallery({ attachments = [], compact = false }) {
  if (!Array.isArray(attachments) || !attachments.length) {
    return null;
  }

  const stickerOnly = attachments.every(
    (attachment) => String(attachment?.kind || "file").toLowerCase() === "sticker"
  );

  return (
    <Stack
      spacing={0.9}
      sx={{
        mt: 0.85,
        width: "100%",
      }}
    >
      {!stickerOnly ? (
        <Typography variant="caption" sx={{ color: "#66788A", fontWeight: 800, letterSpacing: 0.35 }}>
          {attachments.length === 1 ? "Attachment" : "Attachments"}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gap: 0.9,
          gridTemplateColumns: stickerOnly
            ? "repeat(auto-fit, minmax(120px, max-content))"
            : compact
              ? "1fr"
              : { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          justifyContent: stickerOnly ? "flex-start" : "stretch",
        }}
      >
        {attachments.map((attachment) => (
          <AttachmentCard key={attachment?.id || attachment?.name} attachment={attachment} compact={compact} />
        ))}
      </Box>
    </Stack>
  );
}
